import { useTranslation } from "react-i18next";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Globe, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

// Flag components as SVG for better rendering
const FranceFlag = ({ className = "w-6 h-4" }) => (
  <svg className={className} viewBox="0 0 900 600" xmlns="http://www.w3.org/2000/svg">
    <rect width="900" height="600" fill="#ED2939"/>
    <rect width="600" height="600" fill="#fff"/>
    <rect width="300" height="600" fill="#002395"/>
  </svg>
);

const UKFlag = ({ className = "w-6 h-4" }) => (
  <svg className={className} viewBox="0 0 60 30" xmlns="http://www.w3.org/2000/svg">
    <clipPath id="s"><path d="M0,0 v30 h60 v-30 z"/></clipPath>
    <clipPath id="t"><path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z"/></clipPath>
    <g clipPath="url(#s)">
      <path d="M0,0 v30 h60 v-30 z" fill="#012169"/>
      <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6"/>
      <path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#t)" stroke="#C8102E" strokeWidth="4"/>
      <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10"/>
      <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6"/>
    </g>
  </svg>
);

const LanguageSelector = ({ variant = "default", className = "", showLabel = false }) => {
  const { i18n, t } = useTranslation();

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem("i18nextLng", lang);
  };

  // Flags only dropdown - for landing page header
  if (variant === "flagsOnly") {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg hover:bg-white/10 transition-colors ${className}`}
            data-testid="language-selector-flags"
          >
            <div className="w-7 h-5 rounded overflow-hidden shadow-sm border border-white/20">
              {i18n.language === "fr" ? <FranceFlag className="w-full h-full" /> : <UKFlag className="w-full h-full" />}
            </div>
            <ChevronDown className="w-3 h-3 opacity-70" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-0 w-auto">
          <DropdownMenuItem 
            onClick={() => changeLanguage("fr")}
            className={`flex items-center gap-2 cursor-pointer ${i18n.language === "fr" ? "bg-slate-100" : ""}`}
          >
            <div className="w-7 h-5 rounded overflow-hidden shadow-sm border border-slate-200">
              <FranceFlag className="w-full h-full" />
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem 
            onClick={() => changeLanguage("en")}
            className={`flex items-center gap-2 cursor-pointer ${i18n.language === "en" ? "bg-slate-100" : ""}`}
          >
            <div className="w-7 h-5 rounded overflow-hidden shadow-sm border border-slate-200">
              <UKFlag className="w-full h-full" />
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Compact variant for header/navbar
  if (variant === "compact") {
    return (
      <button
        onClick={() => changeLanguage(i18n.language === "fr" ? "en" : "fr")}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors ${className}`}
        title={i18n.language === "fr" ? "Switch to English" : "Passer en français"}
      >
        <Globe className="w-4 h-4" />
        <span className="text-sm font-medium uppercase">{i18n.language}</span>
      </button>
    );
  }

  // Flag variant with flags (buttons side by side)
  if (variant === "flags") {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <button
          onClick={() => changeLanguage("fr")}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
            i18n.language === "fr"
              ? "bg-[#101E4D] text-white"
              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
          }`}
        >
          <div className="w-5 h-3.5 rounded overflow-hidden">
            <FranceFlag className="w-full h-full" />
          </div>
          FR
        </button>
        <button
          onClick={() => changeLanguage("en")}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
            i18n.language === "en"
              ? "bg-[#101E4D] text-white"
              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
          }`}
        >
          <div className="w-5 h-3.5 rounded overflow-hidden">
            <UKFlag className="w-full h-full" />
          </div>
          EN
        </button>
      </div>
    );
  }

  // Default select dropdown
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {showLabel && (
        <span className="text-sm text-slate-600">{t("auth.selectLanguage")} :</span>
      )}
      <Select value={i18n.language} onValueChange={changeLanguage}>
        <SelectTrigger className="w-[160px]" data-testid="language-selector">
          <div className="flex items-center gap-2">
            <div className="w-5 h-3.5 rounded overflow-hidden">
              {i18n.language === "fr" ? <FranceFlag className="w-full h-full" /> : <UKFlag className="w-full h-full" />}
            </div>
            <SelectValue />
          </div>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="fr">
            <span className="flex items-center gap-2">
              <div className="w-5 h-3.5 rounded overflow-hidden">
                <FranceFlag className="w-full h-full" />
              </div>
              {t("auth.french")}
            </span>
          </SelectItem>
          <SelectItem value="en">
            <span className="flex items-center gap-2">
              <div className="w-5 h-3.5 rounded overflow-hidden">
                <UKFlag className="w-full h-full" />
              </div>
              {t("auth.english")}
            </span>
          </SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
};

export default LanguageSelector;
